import { ChevronDownIcon, ChevronRightIcon } from '@calizahq/react-components-v2'
import { ColumnDef } from '@tanstack/react-table'
import { formatInTimeZone } from 'date-fns-tz'
import React from 'react'
import { Link } from 'react-router-dom'

import { Tag } from '@/components'
import { LineTagType } from '@/components/LineItem/LineItem'
import { Priority, Status, TicketListRow } from '@/types/Ticket'

export const buildTicketListColumns = (
  expandedIds: Set<number>,
  onToggleExpand: (id: number) => void,
): ColumnDef<TicketListRow, any>[] => [
  {
    header: '',
    id: 'expand',
    size: 80,
    enableSorting: false,
    cell: function _Cell({ row }: any) {
      const ticket: TicketListRow = row.original
      if (ticket._isChildRow) {
        return (
          <div className="flex h-full w-full items-center justify-center text-tertiary">
            <span className="text-base leading-none">↳</span>
          </div>
        )
      }
      if (!ticket.allowAsParent || !ticket.childTickets?.length) {
        return <div className="w-full" />
      }
      const isExpanded = expandedIds.has(ticket.id)
      const count = ticket.childTickets.length
      return (
        <button
          className="flex w-full items-center justify-center text-tertiary"
          onClick={(e) => {
            e.stopPropagation()
            onToggleExpand(ticket.id)
          }}
        >
          <div className="flex items-center gap-1 whitespace-nowrap">
            {isExpanded ? (
              <ChevronDownIcon className="h-5 w-5 shrink-0" />
            ) : (
              <ChevronRightIcon className="h-5 w-5 shrink-0" />
            )}
            <span className="rounded-full bg-action-primary px-2 py-0.5 text-p-sm text-white">+{count}</span>
          </div>
        </button>
      )
    },
  },
  {
    header: 'ID',
    accessorKey: 'id',
    size: 70,
    enableSorting: false,
    cell: function _Cell({ row }: any) {
      const ticket: TicketListRow = row.original
      return (
        <div className="flex w-full items-center justify-center">
          <Link className="text-p-sm text-tertiary underline" to={`/tickets/${ticket.id}`}>
            #{ticket.id}
          </Link>
        </div>
      )
    },
  },
  {
    header: 'Subject',
    accessorKey: 'subject',
    minSize: 200,
    enableSorting: false,
    cell: function _Cell({ row }: any) {
      const ticket: TicketListRow = row.original
      return (
        <div className="route-link ml-auto flex w-full items-center justify-center">
          <Link className="w-full" to={`/tickets/${ticket.id}`}>
            <p className="pr-2 font-bold text-p-sm underline">{ticket.subject}</p>
            {!ticket._isChildRow && ticket.parentTicket && (
              <p className="text-p-sm text-tertiary">Sub-ticket of #{ticket.parentTicket.id}</p>
            )}
          </Link>
        </div>
      )
    },
  },
  {
    header: 'Market Segment',
    accessorFn: (row) => row.marketSegment?.name,
    id: 'marketSegment.name',
    size: 120,
    cell: function _Cell({ getValue }: any) {
      const value = getValue()
      return (
        <div className="flex w-full items-center justify-center">
          {value && (
            <p
              className="rounded border px-3 py-2  font-bold text-p-sm leading-none border-primary"
              style={{ fontWeight: 700 }}
            >
              {value}
            </p>
          )}
        </div>
      )
    },
  },
  {
    header: 'Priority',
    accessorKey: 'priority',
    size: 100,
    cell: ({ getValue }) => {
      const value = getValue() as Priority
      return (
        <div className="flex w-full items-center justify-center">
          {value && (
            <Tag className="text-p-sm" variant={getPriorityVariant(value)}>
              {value}
            </Tag>
          )}
        </div>
      )
    },
  },
  {
    header: 'Type',
    accessorKey: 'ticketType',
    size: 100,
  },
  {
    header: 'Reason',
    accessorKey: 'ticketReason',
    size: 120,
    enableSorting: false,
  },
  {
    header: 'Status',
    accessorKey: 'internalTicketStatus',
    size: 100,
    cell: ({ getValue }) => {
      const value = getValue() as Priority
      return (
        <div className="flex w-full items-center justify-center">
          <Tag className={getStatusTagClassName(value)} variant="none">
            {value}
          </Tag>
        </div>
      )
    },
  },
  {
    header: 'Owner',
    accessorFn: (row) => row.assignee?.fullName,
    id: 'assignee.fullName',
    size: 100,
    enableSorting: false,
  },
  {
    header: 'Tags',
    id: 'tags',
    enableSorting: false,
    cell: ({ row }: { row: { original: TicketListRow } }) => {
      const value = row.original || {}

      const isStandby = !!value.reservation?.standby
      const isCorporateHousing = !!value.reservation?.professionalHousing
      const isDirect = !!value.home?.property?.landingDirect && !value.home?.property?.isAutopilot
      const isAutoPilot = !!value.home?.property?.isAutopilot

      return (
        <div className="flex w-full items-center justify-center">
          {isCorporateHousing && <Tag className=" mr-1 text-sm text-tag-green-dark tag-green-light">CoHo</Tag>}
          {isStandby && <Tag className="mr-1 text-sm text-tag-grey-dark tag-grey-light">Standby</Tag>}
          {isDirect && <Tag className="text-sm text-tag-yellow-dark tag-yellow-light">Direct</Tag>}
          {isAutoPilot && <Tag className="text-tag-purple-dark tag-purple-light text-sm">Autopilot</Tag>}
        </div>
      )
    },
  },
  {
    header: 'Date Created',
    accessorKey: 'createdAt',
    size: 160,
    cell: function _Cell({ getValue }: any) {
      const value: string = getValue() ?? ''
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      try {
        const time = formatInTimeZone(value, timezone, 'MMM. dd, yyyy, h:mmaaa')
        return time
      } catch (e) {
        return 'Invalid  Date'
      }
    },
  },
  {
    header: 'Last Comment',
    accessorKey: 'lastCommentDatetime',
    size: 160,
    cell: function _Cell({ getValue }: any) {
      const value: string = getValue() ?? ''
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      try {
        const time = formatInTimeZone(value, timezone, 'MMM. dd, yyyy, h:mmaaa')
        return time
      } catch (e) {
        return '--'
      }
    },
  },
]

// Legacy alias kept for any other consumers
export const TicketListColumns = buildTicketListColumns(new Set(), () => {})

export const getPriorityVariant = (value: Priority): LineTagType => {
  if (value === Priority.HIGH) return 'error'
  if (value === Priority.LOW) return 'info'
  return 'warning'
}

export const getStatusTagClassName = (value: string): string => {
  if (value === Status.AWAITING_RESPONSE) return 'bg-action-input text-white text-p-sm'
  if (value === Status.NEEDS_ACTION) return 'bg-page-info text-black text-p-sm'
  if (value === Status.WORKING) return 'bg-page-info text-black text-p-sm'
  if (value === Status.CLOSED) return 'bg-page-warning text-black text-p-sm'
  return 'bg-emphasis text-black text-p-sm'
}
